(() => {
  let countryOptionsRequest = null;

  function hydrateMenuImages(scope) {
    scope.querySelectorAll('template[data-deferred-menu-image]').forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
  }

  function hydrateDeferredImage(wrapper) {
    if (!wrapper || wrapper.dataset.deferredImageLoaded === 'true') return;

    const template = wrapper.querySelector(':scope > template[data-deferred-image-template]');
    if (!template) return;

    template.replaceWith(template.content.cloneNode(true));
    wrapper.dataset.deferredImageLoaded = 'true';
  }

  const deferredImageObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              hydrateDeferredImage(entry.target);
              deferredImageObserver.unobserve(entry.target);
            });
          },
          {rootMargin: '500px 0px', threshold: 0.01}
        )
      : null;

  function hydrateDeferredImages(scope = document) {
    const wrappers = scope.matches?.('[data-deferred-image-wrapper]')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('[data-deferred-image-wrapper]') || []);

    wrappers.forEach((wrapper) => {
      if (wrapper.dataset.deferredImageObserved === 'true') return;
      wrapper.dataset.deferredImageObserved = 'true';
      if (deferredImageObserver) deferredImageObserver.observe(wrapper);
      else hydrateDeferredImage(wrapper);
    });
  }

  function fetchCountryOptions() {
    if (countryOptionsRequest) return countryOptionsRequest;

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', 'deferred-localization-options');

    countryOptionsRequest = fetch(url.toString(), {
      credentials: 'same-origin',
      headers: {'X-Requested-With': 'XMLHttpRequest'},
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Localization options request failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const payload = documentFragment.querySelector('[data-country-options-payload]');
        if (!payload) throw new Error('Localization options payload is missing');

        const countries = JSON.parse(payload.textContent);
        if (!Array.isArray(countries)) throw new Error('Localization options payload is invalid');
        return countries;
      })
      .catch((error) => {
        countryOptionsRequest = null;
        throw error;
      });

    return countryOptionsRequest;
  }

  function createCountryOption(country, activeCode, showFlag) {
    const item = document.createElement('li');
    item.className = 'popout-list__item';
    if (country.code === activeCode) item.classList.add('is-active');

    const option = document.createElement('a');
    option.className = 'popout-list__option';
    option.href = '#';
    option.dataset.value = country.code;
    option.setAttribute('data-popout-option', '');
    if (country.code === activeCode) option.setAttribute('aria-current', 'true');

    if (showFlag && country.flag) {
      const flag = document.createElement('img');
      flag.src = country.flag;
      flag.loading = 'lazy';
      flag.decoding = 'async';
      flag.className = 'popout__flag';
      flag.width = 40;
      flag.height = 40;
      flag.alt = '';
      option.append(flag);
    }

    const label = document.createElement('span');
    label.textContent = `${country.name} (${country.currencyCode} ${country.currencySymbol})`;
    option.append(label);
    item.append(option);
    return item;
  }

  function hydrateDeferredOptions(popout) {
    if (!popout.hasAttribute('data-deferred-country-options') || popout.dataset.countryOptionsHydrated === 'true') return;

    const list = popout.querySelector('[data-popout-list]');
    const input = popout.querySelector('[data-popout-input]');
    if (!list || !input || popout.dataset.countryOptionsLoading === 'true') return;

    popout.dataset.countryOptionsLoading = 'true';
    list.setAttribute('aria-busy', 'true');

    fetchCountryOptions()
      .then((countries) => {
        const options = document.createDocumentFragment();
        const showFlag = popout.dataset.showCountryFlag === 'true';
        countries.forEach((country) => options.append(createCountryOption(country, input.value, showFlag)));
        list.replaceChildren(options);

        popout.popoutOptions = popout.querySelectorAll('[data-popout-option]');
        if (typeof popout._connectOptions === 'function') {
          popout._connectOptions();
        }
        if (typeof popout.popupListSetDimensions === 'function') {
          popout.popupListSetDimensions();
        }

        popout.dataset.countryOptionsHydrated = 'true';
      })
      .catch((error) => {
        console.warn('Unable to load localization options', error);
      })
      .finally(() => {
        delete popout.dataset.countryOptionsLoading;
        list.setAttribute('aria-busy', 'false');
      });
  }

  function hydrateMenuFromEvent(event) {
    const menu = event.target.closest('hover-disclosure, mobile-sliderule');
    if (menu) hydrateMenuImages(menu);
  }

  function playDeferredVideo(video) {
    if (!video) return;
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  function hydrateDeferredVideo(wrapper) {
    if (!wrapper || wrapper.dataset.deferredVideoLoaded === 'true') {
      return wrapper?.querySelector(':scope > video') || null;
    }

    const template = wrapper.querySelector(':scope > template[data-deferred-video-template]');
    if (!template) return null;

    const content = template.content.cloneNode(true);
    const video = content.querySelector('video');
    if (!video) return null;

    video.querySelectorAll('img').forEach((image) => image.remove());
    video.addEventListener(
      'playing',
      () => {
        const poster = wrapper.querySelector(':scope > img');
        if (poster) poster.hidden = true;
      },
      {once: true}
    );

    wrapper.append(content);
    wrapper.dataset.deferredVideoLoaded = 'true';
    playDeferredVideo(video);
    return video;
  }

  const deferredVideoObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const wrapper = entry.target;
              if (entry.isIntersecting) {
                playDeferredVideo(hydrateDeferredVideo(wrapper));
              } else {
                wrapper.querySelector(':scope > video')?.pause();
              }
            });
          },
          {rootMargin: '700px 0px', threshold: 0.01}
        )
      : null;

  function initDeferredVideos(scope = document) {
    const wrappers = scope.matches?.('[data-deferred-autoplay-video]')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('[data-deferred-autoplay-video]') || []);

    wrappers.forEach((wrapper) => {
      if (wrapper.dataset.deferredVideoObserved === 'true') return;
      wrapper.dataset.deferredVideoObserved = 'true';
      if (deferredVideoObserver) {
        deferredVideoObserver.observe(wrapper);
      } else {
        hydrateDeferredVideo(wrapper);
      }
    });
  }

  document.addEventListener('pointerover', hydrateMenuFromEvent, true);
  document.addEventListener('focusin', hydrateMenuFromEvent, true);
  document.addEventListener('touchstart', hydrateMenuFromEvent, {capture: true, passive: true});
  document.addEventListener(
    'click',
    (event) => {
      hydrateMenuFromEvent(event);
      const toggle = event.target.closest('[data-popout-toggle]');
      const popout = toggle && toggle.closest('popout-select');
      if (popout) {
        hydrateDeferredOptions(popout);
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        hydrateDeferredImages(document);
        initDeferredVideos(document);
      },
      {once: true}
    );
  } else {
    hydrateDeferredImages(document);
    initDeferredVideos(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    hydrateDeferredImages(event.target);
    initDeferredVideos(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (deferredImageObserver) {
      event.target
        .querySelectorAll?.('[data-deferred-image-wrapper]')
        .forEach((wrapper) => deferredImageObserver.unobserve(wrapper));
    }
    if (deferredVideoObserver) {
      event.target
        .querySelectorAll?.('[data-deferred-autoplay-video]')
        .forEach((wrapper) => deferredVideoObserver.unobserve(wrapper));
    }
  });
})();
